/* eslint-disable monorepo/no-internal-import */
import {parseRequestProperties} from '@aboutyou/bootstrap/src/parseRequestProperties';
import {createStaticConfig} from '@aboutyou/bootstrap/src/staticConfig/createStaticConfig';
import {StaticConfig} from '@aboutyou/bootstrap/src/staticConfig/types';
import type {Locale} from '@aboutyou/config';
import {getVersionName} from '@aboutyou/config';
import {
  DD_BROWSER_LOGGERS,
  DD_ENV_NAME_MAP,
  FALLBACK_LOGS_MIDDLEWARE_ROUTE,
  FALL_LOGS_TOKEN,
  LOGS_TOKEN_HEADER,
  Log,
} from '@aboutyou/mlm';
import type {Request, Response} from 'express';
import React from 'react';
import {renderToString} from 'react-dom/server';
import {SSO_CALLBACK_EVENT, SSO_CALLBACK_ROUTE} from './constants';
// eslint-disable-next-line no-restricted-imports
import {translateGlobal} from '@aboutyou/i18n';
import {FallbackLog} from '@aboutyou/mlm/src/fallbackLogsMiddleware/fallbackLogType';
import {initializeDeviceSpecificsForLogging} from '@aboutyou/utils/src/initializeDeviceSpecificsForLogging';

const ssoCallbackScript = () => {
  return ({
    SSO_CALLBACK_EVENT,
    errorTranslations,
    accountPageRoute,
    env,
    locale,
    shop_id,
    version,
    device,
    hostname,
    fallbackLogsMiddlewareRoute,
    loggerLevel,
    logsToken,
    logsTokenHeader,
  }: {
    SSO_CALLBACK_EVENT: string;
    errorTranslations: {title: string; message: string};
    accountPageRoute: string;
    env: string;
    locale: Locale;
    shop_id?: number;
    version: string;
    device: 'mobile' | 'desktop';
    hostname: string;
    fallbackLogsMiddlewareRoute: string;
    loggerLevel: DD_BROWSER_LOGGERS;
    logsToken: string;
    logsTokenHeader: string;
  }) => {
    const searchParams = new URLSearchParams(window.location.search);
    const searchParamsMap = Object.fromEntries(searchParams.entries());

    function bufferToHex(buffer: ArrayBuffer) {
      const bytes = new Uint8Array(buffer);
      return Array.from(bytes)
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
    }

    async function getPayloadSignature(data: Partial<FallbackLog>) {
      try {
        const encoder = new TextEncoder();
        // Import the secret key for use with HMAC-SHA256.
        const key = await crypto.subtle.importKey(
          'raw',
          encoder.encode(logsToken),
          {name: 'HMAC', hash: 'SHA-256'},
          false,
          ['sign', 'verify'],
        );

        const signatureBuffer = await crypto.subtle.sign(
          'HMAC',
          key,
          encoder.encode(JSON.stringify(data)),
        );

        // Convert the signature to a hex string.
        return bufferToHex(signatureBuffer);
      } catch (error) {
        return '';
      }
    }

    const getAttributesFromState = (state?: string | null) => {
      if (!state) {
        return null;
      }

      try {
        const decodedState = JSON.parse(atob(state)) as {
          idpKey?: string;
          idp?: string;
          log_instance_id?: string;
        };

        return decodedState;
      } catch (error) {
        return null;
      }
    };

    const logEvents = async (
      partialPayload: Partial<FallbackLog>,
      keepalive: boolean,
    ) => {
      const tab_id = searchParams.get('tab_id');
      const state = searchParams.get('state');

      const attributesFromState = getAttributesFromState(state);

      const payload: Partial<FallbackLog> = {
        ...partialPayload,
        meta_data: {
          device: `${device}-web`,
          shop_id,
          locale,
        },
        feature: 'auth',
        shop_id, // can be deleted after a month or two
        ddtags: `env:'${env}',version:'${version}'`,
        env,
        device: `${device}-web`, // can be deleted after a month or two
        method: 'sso',
        idp: attributesFromState?.idp,
        idp_key: attributesFromState?.idpKey,
        log_instance_id: attributesFromState?.log_instance_id,
        view: {
          referrer: document.referrer,
          url: window.location.href,
          url_details: {
            host: hostname,
            path: window.location.pathname,
            queryString: Object.fromEntries(
              new URLSearchParams(window.location.search),
            ),
          },
        },
        http: {
          detected: {
            device: {
              category: device === 'mobile' ? 'Mobile' : 'Desktop',
              model: window.__DEVICE_DETAILS__?.deviceModel,
              brand: window.__DEVICE_DETAILS__?.browserBrands,
            },
            browser: {
              family: window.__DEVICE_DETAILS__?.browserName,
              major: window.__DEVICE_DETAILS__?.browserVersion,
            },
            os: {
              family: window.__DEVICE_DETAILS__?.osName,
              major: window.__DEVICE_DETAILS__?.osVersion,
            },
          },
          useragent: window.navigator?.userAgent,
          appVersion: window.navigator?.appVersion,
        },
        level: loggerLevel,
        logger: {name: loggerLevel},
        online: window.navigator?.onLine ?? 'unknown',
        tab_id,
      };

      const logsSignature = await getPayloadSignature(payload);

      fetch(fallbackLogsMiddlewareRoute, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          [logsTokenHeader]: logsSignature,
        },
        body: JSON.stringify(payload),
        keepalive,
      });
    };

    const logAutomaticRedirect = async () => {
      const payload: Partial<FallbackLog> = {
        message: 'sso-go-to-shop-redirect',
        event: 'sso-go-to-shop-redirect',
      };
      logEvents(payload, true);
    };

    const logRedirectFailed = async () => {
      const payload: Partial<FallbackLog> = {
        message: 'sso-go-to-shop-redirect-failed',
        event: 'sso-go-to-shop-redirect-failed',
        old_message: 'SSOStuckPopup',
      };

      logEvents(payload, false);
    };

    const tryClosingThePopupOrRedirect = () => {
      // we try to close the popup window, but it might fail due to browser security settings
      // As a fallback we redirect to the account page with a small delay
      window.close();

      setTimeout(() => {
        logAutomaticRedirect();
        window.location.href = accountPageRoute;

        // If after another 2 seconds the popup is still open, we log that the redirect failed
        setTimeout(() => {
          logRedirectFailed();
        }, 2000);
      }, 1500);
    };

    if (window.opener) {
      window.opener.postMessage(
        {
          type: SSO_CALLBACK_EVENT,
          data: searchParamsMap,
        },
        window.location.origin,
      );

      tryClosingThePopupOrRedirect();
    } else if (typeof BroadcastChannel !== 'undefined') {
      const sso_channel = searchParams.get('sso_channel')!; // this param is always set by us during SSO buttons fetch
      const channel = new window.BroadcastChannel(sso_channel);
      channel.postMessage({type: SSO_CALLBACK_EVENT, data: searchParamsMap});
      channel.close();

      tryClosingThePopupOrRedirect();
    } else {
      window.document.body.innerHTML = `
      <h2>${errorTranslations.title}</h2>
      <p>${errorTranslations.message}</p>
      `;
    }
  };
};

const SSOInterceptScript: React.FC<{
  errorTranslations: {
    title: string;
    message: string;
  };
  accountPageRoute: string;
  env: string;
  locale: Locale;
  version: string;
  device: 'mobile' | 'desktop';
  hostname: string;
  shop_id?: number;
}> = ({
  errorTranslations,
  env,
  locale,
  version,
  device,
  hostname,
  shop_id,
  accountPageRoute,
}) => {
  return (
    <html>
      <head>
        <title>Authentication</title>
        <meta charSet="UTF-8"></meta>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0"
        ></meta>
        {style}
      </head>
      <body>
        <script
          dangerouslySetInnerHTML={{
            __html: `
              (${ssoCallbackScript().toString()})(
              {
                SSO_CALLBACK_EVENT: "${SSO_CALLBACK_EVENT}",
                errorTranslations: ${JSON.stringify(errorTranslations)},
                accountPageRoute: "${accountPageRoute}",
                env: "${env}",
                locale: "${locale}",
                version: "${version}",
                shop_id: ${shop_id},
                device: "${device}",
                hostname: "${hostname}",
                fallbackLogsMiddlewareRoute: "${FALLBACK_LOGS_MIDDLEWARE_ROUTE}",
                loggerLevel: "${DD_BROWSER_LOGGERS.UNSAMPLED_LOGS}",
                logsToken: "${FALL_LOGS_TOKEN}",
                logsTokenHeader: "${LOGS_TOKEN_HEADER}",
              });
          `,
          }}
        ></script>

        <script
          id="initialize-device-details-script"
          dangerouslySetInnerHTML={{
            __html: `
            (${initializeDeviceSpecificsForLogging.toString()})();
          `,
          }}
        ></script>
        <div className="spinner-container">
          <div className="spinner">
            <div className="spinner-ring spinner-ring-1"></div>
            <div className="spinner-ring spinner-ring-2"></div>
            <div className="spinner-ring spinner-ring-3"></div>
          </div>
        </div>
      </body>
    </html>
  );
};

const style = (
  <style>
    {`
body {
    margin: 0;
    padding: 20px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 100vh;
    font-family: Inter, sans-serif;
    text-align: center;
    height: 75vh;
  }

  .spinner-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    margin-top: 50px;
  }

  .spinner {
    display: inline-block;
    position: relative;
    width: 4rem;
    height: 4rem;
  }

  .spinner-ring {
    box-sizing: border-box;
    position: absolute;
    width: 51px;
    height: 51px;
    margin: 6px;
    border-radius: 50%;
    border: 4px solid #000;
    animation: spinner-frames 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
    border-color: #000 transparent transparent transparent;
  }

  .spinner-ring-1 {
    animation-delay: -0.45s;
  }

  .spinner-ring-2 {
    animation-delay: -0.3s;
  }

  .spinner-ring-3 {
    animation-delay: -0.15s;
  }

  @keyframes spinner-frames {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`}
  </style>
);

export const ssoCallbackMiddleware = async (req: Request, res: Response) => {
  Log.transact('GET /__/sso_callback', {timestamp: String(Date.now())});

  const {hostname, enableRealUserMode, abTestGroups, traceId} =
    parseRequestProperties({req});

  let staticConfig: StaticConfig;

  try {
    staticConfig = await createStaticConfig({
      traceId,
      originalRequest: req,
      hostname,
      abTestGroups,
      enableRealUserMode,
    });
  } catch (error) {
    return res.status(400).send('Bad Request');
  }

  const hostConfig = staticConfig.hostConfig;

  const mobileHost = `${hostConfig?.publicProtocol}://${hostConfig?.hostname}${
    hostConfig?.publicPort ? `:${hostConfig?.publicPort}` : ''
  }`;

  const desktopHostname =
    hostConfig && 'publicDesktopHostname' in hostConfig
      ? (hostConfig.publicDesktopHostname as string)
      : req.hostname;

  const desktopHost = `${hostConfig?.publicProtocol}://${desktopHostname}${
    hostConfig?.publicPort ? `:${hostConfig?.publicPort}` : ''
  }`;

  if (!hostConfig) {
    res.status(400).send('Bad Request: Unknown Host');
    return;
  }

  const t = await translateGlobal(req.hostname);

  const errorTranslations = {
    title: t('Oops! Something went wrong!'),
    message: t(
      'Sorry, your browser is not supported. Please upgrade your browser or operating system, use another browser or log in via e-mail.',
    ),
  };

  const env = DD_ENV_NAME_MAP[staticConfig.hostConfig.environment];
  const version = getVersionName();
  const device = staticConfig.hostConfig.device;
  const shop_id = staticConfig.hostConfig.legacyConfig?.shopId;
  const locale = staticConfig.hostConfig.locale;

  const accountPageRoute =
    staticConfig.routesConfig.pageTypePathsMap.account[0]!;

  try {
    const reactHtml = renderToString(
      <SSOInterceptScript
        errorTranslations={errorTranslations}
        env={env}
        version={version}
        device={device}
        hostname={hostname}
        accountPageRoute={accountPageRoute}
        shop_id={shop_id}
        locale={locale}
      />,
    );

    const fullHtml = `<!DOCTYPE html>${reactHtml}`;

    res.setHeader(
      'Content-Security-Policy',
      `frame-ancestors 'self' ${mobileHost} ${desktopHost}`,
    );
    res.setHeader('Content-Type', 'text/html');

    res.status(200).send(fullHtml);
  } catch (error) {
    Log.error('@aboutyou/ssoCallbackMiddleware during renderToString:', error);
    if (!res.headersSent) {
      res.status(500).send('Server Error during rendering');
    }
  }
};

ssoCallbackMiddleware.PUBLIC_ROUTE = SSO_CALLBACK_ROUTE;
